import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

import CautionIcon from '~/shared/assets/icons/caution.svg';
import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  isSaving?: boolean;
}

export default function UnsavedChangesModal({
  isOpen,
  onClose,
  onSave,
  isSaving,
}: UnsavedChangesModalProps) {
  return (
    <Dialog open={isOpen} as="div" className="relative z-[99] focus:outline-none" onClose={onClose}>
      <div className="fixed inset-0 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px]">
        <div className="flex items-center justify-center h-full">
          <DialogPanel
            transition
            className="flex w-full max-w-[689px] flex-col rounded-xl bg-white p6 backdrop-blur-2xl duration-300 ease-out overflow-hidden data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="p-6">
              <DialogHeader
                title="You have unsaved changes"
                description="Are you sure you want to leave without saving?"
              />
            </div>

            <DialogFooter onCancel={onClose} onSave={onSave} isSaving={isSaving} />
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function DialogHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col font-family-outfit items-center text-center">
      <CautionIcon className="size-10 text-red " />
      <DialogTitle as="h3">
        <Typography variant="H3-med" fontWeight={600} className="mt-6 font-family-outfit">
          {title}
        </Typography>
      </DialogTitle>
      <Typography variant="p3-reg" className="mt-2 text-center max-w-[536px]">
        {description}
      </Typography>
    </div>
  );
}

function DialogFooter({
  onCancel,
  onSave,
  isSaving,
}: {
  onCancel: () => void;
  onSave: () => void | Promise<void>;
  isSaving?: boolean;
}) {
  const handleSave = async () => {
    await onSave();
  };

  return (
    <div className="mt-auto flex items-center justify-center gap-2 p-6">
      <div className="flex gap-2">
        <Button onClick={onCancel} variant="third" className="px-5" disabled={isSaving}>
          Exit without saving
        </Button>
        <Button onClick={handleSave} className="px-7" disabled={isSaving}>
          {isSaving ? 'Saving changes...' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
